import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AOI, ApiGeometryType, LatLong, MapLayer } from '../../models/Geometries';
import { calculateArea, generateBounds, generateRectangle } from '../../utils/GeometryUtils';
import { message } from 'antd';
import { loadGeometries, MapState } from './mapSlice';
import { RootState } from '../store';
import { GeometryService } from '../services/map/geometry.service';
import { Adjuntos } from '../../models/Adjuntos';


export interface MapLayersState {
  error?: string;
  addingAOI: boolean;
  firstPoint?: LatLong;
  secondPoint?: LatLong;
  aoi?: AOI;
  aoiVisible: boolean;
  aoiSelected: boolean;
  loading: boolean;
  aoiTutorial: boolean;
}

const initialState: MapLayersState = {
  addingAOI: false,
  aoiVisible: true,
  aoiSelected: false,
  loading: false,
  aoiTutorial: false,
};

export const mapAOIlice = createSlice({
    name: 'mapAOI',
    initialState,
    reducers: {
      setAddingAOI: (state, arg: PayloadAction<boolean>) => {
        state.addingAOI = arg.payload;
        if(state.addingAOI){
            state.aoiTutorial = !localStorage.getItem('aoiTutorialShown');
        }
        state.firstPoint = undefined;
        state.secondPoint = undefined;
        state.aoi = undefined;
        state.aoiVisible = true;
        state.aoiSelected = false;
      },
      closeAOITutorial: (state) => {
        state.aoiTutorial = false;
      },
      setAOIVisible: (state, arg: PayloadAction<boolean>) => {
        state.aoiVisible = arg.payload;
      },
      setAOISelected: (state, arg: PayloadAction<boolean>) => {
        state.aoiSelected = arg.payload;
      },
      addAOIPoint: (state, arg: PayloadAction<{ coord: LatLong, layers: MapLayer[] }>) => {

        if (state.firstPoint === undefined) {
          state.firstPoint = arg.payload.coord;
        } else {
          // const layers = arg.payload.layers.filter(l => l.type !== 'Catastro Minero');
          const layers = arg.payload.layers.filter(l => l.type !== process.env.REACT_APP_AOI_GEOSERVER_LAYER_EXCLUDED);

          if (calculateArea(generateRectangle(state.firstPoint, arg.payload.coord).coordinates[0])
            > parseInt(process.env.REACT_APP_MAX_AOI_AREA_KM2 as string)) {
            message.error('El tamaño del área de interés excede el limite permitido.');
            return;
          }

          state.secondPoint = arg.payload.coord;
          state.addingAOI = false;
          state.aoi = {
            layers: layers,
            area: generateRectangle(state.firstPoint, state.secondPoint),
            bounds: generateBounds(state.firstPoint, state.secondPoint),
            createdAt: new Date().getTime(),
          };
        }
      },
    },
    extraReducers: (builder) => {
      builder.addCase(loadGeometries.fulfilled, (state, action) => {
        action.payload.forEach(apiGeometry => {
          if (apiGeometry.tipo === ApiGeometryType.AOI) {
            state.aoi = {
              id: apiGeometry.id,
              area: apiGeometry.geometria,
              bounds: apiGeometry.atributos.aoiBounds,
              layers: apiGeometry.atributos.aoiLayers,
              createdAt: new Date(apiGeometry.createdAt).getTime(),
              adjunto: apiGeometry.adjuntos?.[0],
            };
            state.firstPoint = apiGeometry.atributos.firstPoint;
            state.secondPoint = apiGeometry.atributos.secondPoint;
          }
        });
      });
      builder.addCase(saveAOI.pending, (state, action) => {
        state.error = undefined;
        state.loading = true;
      });
      builder.addCase(saveAOI.rejected, (state, action) => {
        state.aoi = undefined;
        state.loading = false;
        message.error('Error al guardar área de interés')
      });
      builder.addCase(saveAOI.fulfilled, (state, action) => {
        if(action.payload?.geometry && state.aoi){
          state.aoi = {...state.aoi, adjunto: action.payload.attch, id: action.payload.geometry.id}
        }
        state.loading = false;
      });
      builder.addCase(deleteAOI.pending, (state, action) => {
        state.error = undefined;
      });
      builder.addCase(deleteAOI.fulfilled, (state, action) => {
        state.aoi = undefined;
      });
      builder.addCase(deleteAOI.rejected, (state, action) => {
        message.error('Error al eliminar área de interés');
      });
    },
  })
;


export const saveAOI = createAsyncThunk(
  'save_aoi',
  async (
    payload: void,
    thunkApi,
  ) => {
    const aoi: AOI | undefined = (thunkApi.getState() as RootState).mapAOI.aoi;
    if (!aoi)
      return;

    try {
      const geometry = await GeometryService.saveGeometry({
        titulo: 'Área de Interés',
        tipo: ApiGeometryType.AOI,
        atributos: {
          aoiBounds: aoi.bounds,
          aoiLayers: aoi.layers,
          firstPoint: (thunkApi.getState() as RootState).mapAOI.firstPoint,
          secondPoint: (thunkApi.getState() as RootState).mapAOI.secondPoint,
        },
        geometria: aoi.area,
        tareaId: (thunkApi.getState() as RootState).task.currentTask!.id!,
      });
      const attch: Adjuntos[] = await GeometryService.uploadAttachment(geometry, aoi);
      return {geometry, attch: attch[0]};
    } catch (e: any) {
      return thunkApi.rejectWithValue(e.response.data);
    }
  },
);


export const deleteAOI = createAsyncThunk(
  'delete_aoi',
  async (
    payload: void,
    thunkApi,
  ) => {
    const aoi: AOI | undefined = (thunkApi.getState() as RootState).mapAOI.aoi;
    if (!aoi)
      return;
    try {
      if(aoi.adjunto){
        await GeometryService.deleteAttachment(aoi.adjunto.id)
      }
      return await GeometryService.deleteGeometry(aoi.id!);
    } catch (e: any) {
      return thunkApi.rejectWithValue(e.response.data);
    }
  },
);


export const {
  addAOIPoint,
  setAddingAOI,
  setAOIVisible,
  setAOISelected,
  closeAOITutorial,
} = mapAOIlice.actions;

export default mapAOIlice.reducer;
